function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { compact, isEqual } from 'lodash-es';
import { Callout, DropdownMenu, DropdownMenuItem, IconButton, Spinner } from 'seagull';
import { Edit2Liner24, MoreVertical, Void } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { getServiceChangeBullets } from "../../../business-logic/amendmentsLogic";
import { isFutureEffectiveDate } from "../../../business-logic/billingLogic";
import EditServiceContentModal from "../../EditServiceContentModal/EditServiceContentModal";
import { useOverlayContext, useIoContext, useStoreActions } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { handleRequest } from "../../../infra/utils";
import { BillingDayOfMonthModel, invoiceDraftModel, serviceBillingModel, serviceModel } from "../../../models";
import { fromDateStampObject } from "../../../models/time";
import AmendmentChanges from "../../AmendmentChanges/AmendmentChanges";
import { useServiceAdvancedSettingsAdditionalSection } from "../../ServiceAdvancedSettings";
import ServiceBuilder from "../../ServiceCreation/ServiceBuilder/ServiceBuilder";
import ServicePanel from "../../ServicePanel/ServicePanel";
import TerminateServiceModal from "../../TerminateServiceModal";
import { changeServicePriceAndDiscount, handleDiscountExpiration, disableEditBillingInitiation, getAmendedNextBillingDateText, getServiceAutoApprovalDate, shouldShowDiscountExpirationWarning, showOnApprovalInfo } from "../utils/EditAgreementModal.utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const disabledOneTimeInStagesTooltip = "Services set to 'Bill in stages' can't be modified. To make changes, remove this service and add a new one to the agreement.";
export const getEditAgreementServiceTID = parentTestId => getTestIds({
  parentTestId,
  deprecatedTestIds: {
    EditAgreementServiceTestIds: {
      ServiceAdvancedSettings: testPrefix => testPrefix,
      OnApprovalServiceInfo: testPrefix => "".concat(testPrefix, "-onApproval-service-info"),
      CalloutDiscountExpiration: testPrefix => "".concat(testPrefix, "-callout-discount-expiration"),
      AmendedNextBillingDate: testPrefix => "".concat(testPrefix, "-amended-next-billing-date"),
      AmendedNextBillingDateLoading: testPrefix => "".concat(testPrefix, "-amended-next-billing-date-loading"),
      Changes: testPrefix => "".concat(testPrefix, "-changes"),
      Menu: testPrefix => "".concat(testPrefix, "-menu"),
      Edit: testPrefix => "".concat(testPrefix, "-edit"),
      Stop: testPrefix => "".concat(testPrefix, "-stop")
    }
  }
});
export const EditAgreementService = /*#__PURE__*/forwardRef(function EditAgreementService(props, ref) {
  const {
    service,
    relationship,
    agreementData,
    showServiceValidationWarning,
    originalService,
    onServiceChange,
    onRemoveService,
    idx,
    testId
  } = props;
  const {
    EditAgreementServiceTestIds
  } = useMemo(() => getEditAgreementServiceTID(testId), [testId]);
  const {
    getNow
  } = useIoContext();
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const featureFlags = useFeatureFlags();
  const {
    SERVICE_TEMPLATE_BUNDLE = false
  } = featureFlags;
  const [amendedNextBillingDate, setAmendedNextBillingDate] = useState();
  const [loadingAmendedNextBillingDate, setLoadingAmendedNextBillingDate] = useState(false);
  const isUnsaved = !isEqual(originalService, service);
  const isFreshlyAdded = !originalService;
  const disableStatusBadge = serviceModel.isAmended(service.status) && !!originalService && !serviceModel.isAmended(originalService.status);
  const isExistingOneTimeInStages = serviceBillingModel.isOneTimeInStages(service.billing) && !isFreshlyAdded;
  const isBundle = serviceModel.isBundle(service);
  const updateService = useCallback(updater => onServiceChange(service.id, updater), [service.id, onServiceChange]);
  const shouldShowDiscountExpirationCallout = shouldShowDiscountExpirationWarning(service, originalService);
  const changeBullets = useMemo(() => getServiceChangeBullets(service, SERVICE_TEMPLATE_BUNDLE), [service, SERVICE_TEMPLATE_BUNDLE]);
  const autoApprovalDate = useMemo(() => getServiceAutoApprovalDate(service, relationship, getNow()), [service, relationship]);
  const onStartBillingAtChange = useCallback((serviceId, startBillingAt) => {
    if (serviceId !== service.id) return;
    // todo: next step - implement for amended services
    if (!serviceModel.isAwaitingApproval(service.status)) return;
    updateService(current => serviceModel.setStartBillingAt(current, startBillingAt));
  }, [service.id, service.status, updateService]);
  const handleServiceChange = useCallback(updatedService => {
    onServiceChange(service.id, () => updatedService);
  }, [service.id, onServiceChange]);
  const serviceAdvancedSettingsAdditionalSection = useServiceAdvancedSettingsAdditionalSection({
    relationshipState: {
      type: 'approved',
      effectiveDate: relationship.effectiveDate ? fromDateStampObject(relationship.effectiveDate) : null
    },
    service: serviceModel.applyChanges(service),
    originalService: service,
    testId: EditAgreementServiceTestIds.ServiceAdvancedSettings(testId),
    showServiceValidationWarning,
    isReadOnly: false,
    onPausedChange,
    onBillOnAcceptanceChange,
    onProratedChange,
    onLimitBillingTimesChange,
    onAutoPriceIncreaseChange,
    onQboClassChange,
    onDiscountPeriodExpirationChange,
    onPreapprovedAmountChange,
    onStartBillingAtChange,
    onServiceChange: handleServiceChange,
    isFreshlyAdded
  });
  async function onEditService() {
    logBI(11005, {
      name: service.name,
      id: service.id
    });
    const editedService = await overlayContext.showModal({
      closeOnEscapeKey: true,
      content: p => /*#__PURE__*/_jsx(ServiceBuilder, _objectSpread({
        applyOutputToChanges: !serviceModel.isAwaitingApproval(service.status),
        service: serviceModel.applyChanges(service),
        serviceOrigin: "agreement",
        agreementData: agreementData
      }, p))
    });
    if (!editedService) return;
    if (serviceModel.isAwaitingApproval(service.status)) {
      handleAwaitingApprovalService(editedService);
    } else {
      handleApprovedService(editedService);
    }
  }
  async function onEditServiceNameAndDescription() {
    await overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/_jsx(EditServiceContentModal, {
          width: "900px",
          title: "".concat(isBundle ? 'Bundle' : 'Service', " name & description"),
          serviceName: service.name,
          description: service.description,
          serviceId: service.id,
          dismissModal: dismissModal,
          onSave: _ref2 => {
            let {
              name,
              description
            } = _ref2;
            updateService(current => _objectSpread(_objectSpread({}, current), {}, {
              name,
              description
            }));
            dismissModal();
          },
          bundle: service.bundle
        });
      }
    });
  }
  function handleAwaitingApprovalService(editedService) {
    logBI(11011, {
      id: editedService.id,
      name: editedService.name
    });
    onServiceChange(editedService.id, () => editedService);
  }
  function handleApprovedService(editedService) {
    const updatedService = handleDiscountExpiration(editedService, originalService);
    const withChanges = serviceModel.applyChanges(updatedService);
    logBI(11011, {
      id: withChanges.id,
      name: withChanges.name
    });
    const newChanges = serviceModel.changes(updatedService);
    if (newChanges) {
      updateService(current => serviceModel.setChanges(current, newChanges));
    }
  }
  function onStopService() {
    overlayContext.showModal({
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/_jsx(TerminateServiceModal, {
          serviceName: service.name,
          agreementId: relationship.id,
          stoppageMayAffectNextBill: serviceBillingModel.isOneTimeManual(service.billing) || serviceBillingModel.isRepeatableManual(service.billing),
          canPauseService: serviceBillingModel.isOngoing(service.billing),
          onKeepService: () => {
            dismissModal();
            updateService(current => serviceModel.setTerminated(current));
          },
          onStopService: dismissModal
        });
      }
    });
  }
  function onProratedChange(serviceId, prorated) {
    if (serviceId !== service.id) return;
    updateService(current => serviceModel.setProrated(current, prorated));
  }
  function onNoteChange(note) {
    updateService(current => serviceModel.setNote(current, note));
  }
  function onPausedChange(serviceId, paused) {
    if (serviceId !== service.id) return;
    updateService(current => serviceModel.setPaused(current, paused));
  }
  function onBillOnAcceptanceChange(serviceId, billOnAcceptance) {
    if (serviceId !== service.id) return;
    updateService(current => serviceModel.setSkipBillingOnAcceptance(current, !billOnAcceptance, featureFlags));
  }
  function onLimitBillingTimesChange(serviceId, enabled, limit) {
    if (serviceId !== service.id) return;
    updateService(current => enabled ? serviceModel.setMaxCharges(current, enabled, limit) : serviceModel.deleteMaxCharges(current));
  }
  function onAutoPriceIncreaseChange(serviceId, priceIncrease) {
    if (serviceId !== service.id) return;
    updateService(current => serviceModel.isAwaitingApproval(current.status) ? serviceModel.setPriceIncrease(current, priceIncrease) : serviceModel.setPriceIncreaseChanges(current, priceIncrease));
  }
  function onBillUpfrontModeChange(serviceId, billUpfront) {
    if (serviceId !== service.id) return;
    updateService(current => serviceModel.setBillUpfront(current, billUpfront));
  }
  function onIncludedServicesChange(includedServices) {
    updateService(current => serviceModel.isAwaitingApproval(current.status) ? serviceModel.setIncludedServices(current, includedServices) : serviceModel.setIncludedServicesChanges(current, includedServices));
  }
  function onDayOfMonthChange(serviceId, dayOfMonth) {
    var _updateService;
    if (serviceId !== service.id) return;
    const newService = (_updateService = updateService(current => serviceModel.isAwaitingApproval(current.status) ? serviceModel.setBillingDayOfMonth(current, dayOfMonth) : serviceModel.setBillingDayOfMonthChanges(current, dayOfMonth))) !== null && _updateService !== void 0 ? _updateService : service;
    if (originalService && !BillingDayOfMonthModel.isEqual(serviceModel.billingDayOfMonth(serviceModel.applyChanges(originalService)), serviceModel.billingDayOfMonth(serviceModel.applyChanges(newService)))) {
      fetchAndSetAmendedNextBillingDate(newService);
    } else {
      clearAmendedNextBillingDate();
    }
  }
  function onDayOfWeekChange(serviceId, dayOfWeek) {
    var _updateService2;
    if (serviceId !== service.id) return;
    const newService = (_updateService2 = updateService(current => serviceModel.isAwaitingApproval(current.status) ? serviceModel.setBillingDayOfWeek(current, dayOfWeek) : serviceModel.setBillingDayOfWeekChanges(current, dayOfWeek))) !== null && _updateService2 !== void 0 ? _updateService2 : service;
    if (originalService && serviceModel.billingDayOfWeek(serviceModel.applyChanges(originalService)) !== serviceModel.billingDayOfWeek(serviceModel.applyChanges(newService))) {
      fetchAndSetAmendedNextBillingDate(newService);
    } else {
      clearAmendedNextBillingDate();
    }
  }
  function onDayOfYearChange(serviceId, dayOfYear) {
    var _updateService3;
    if (serviceId !== service.id) return;
    const newService = (_updateService3 = updateService(current => serviceModel.isAwaitingApproval(current.status) ? serviceModel.setBillingDayOfYear(current, dayOfYear) : serviceModel.setBillingDayOfYearChanges(current, dayOfYear))) !== null && _updateService3 !== void 0 ? _updateService3 : service;
    if (originalService && serviceModel.billingDayOfYear(serviceModel.applyChanges(originalService)) !== serviceModel.billingDayOfYear(serviceModel.applyChanges(newService))) {
      fetchAndSetAmendedNextBillingDate(newService);
    } else {
      clearAmendedNextBillingDate();
    }
  }
  function onQboClassChange(serviceId, integratedClass) {
    if (serviceId !== service.id) return;
    updateService(current => serviceModel.setIntegratedClass(current, integratedClass));
  }
  function onDiscountPeriodExpirationChange(serviceId, discountPeriodExpiration) {
    if (serviceId !== service.id) return;
    updateService(current => serviceModel.setDiscountPeriodExpiration(current, discountPeriodExpiration));
  }
  function onPreapprovedAmountChange(serviceId, amount, controlledByAdvancedSettings) {
    if (serviceId !== service.id) return;
    updateService(current => serviceModel.isAwaitingApproval(current.status) ? serviceModel.setPreapprovedAmount(current, amount, controlledByAdvancedSettings) : serviceModel.setPreapprovedAmountChanges(current, amount, controlledByAdvancedSettings));
  }
  function onDiscardChanges() {
    if (serviceModel.isAwaitingApproval(service.status)) {
      if (originalService) {
        logBI(11007, {
          id: service.id,
          name: service.name
        });
        updateService(current => serviceModel.setCanceled(current));
      } else {
        onRemoveService(service.id);
      }
    } else {
      logBI(11012, {
        id: service.id,
        name: service.name
      });
      updateService(current => serviceModel.discardChanges(current));
    }
    clearAmendedNextBillingDate();
  }
  async function fetchAndSetAmendedNextBillingDate(service) {
    setLoadingAmendedNextBillingDate(true);
    const result = await handleRequest(api.invoiceDraft.getForAmendedService(relationship.id, service));
    if (!result.success) {
      clearAmendedNextBillingDate();
    } else {
      const {
        issueDate,
        lineItem
      } = result.data;
      const isOverlappingPeriod = invoiceDraftModel.isOverlappingPeriod(lineItem);
      if (isOverlappingPeriod) {
        setAmendedNextBillingDate(issueDate);
      } else {
        clearAmendedNextBillingDate();
      }
    }
    setLoadingAmendedNextBillingDate(false);
  }
  function clearAmendedNextBillingDate() {
    if (amendedNextBillingDate) setAmendedNextBillingDate(null);
  }
  return /*#__PURE__*/_jsx(ServicePanel, {
    serviceOrigin: "agreement",
    onEditServiceNameAndDescription: onEditServiceNameAndDescription,
    context: "agreement",
    showBadges: true,
    isVendor: true,
    billingInitiationProps: {
      isExistingService: !!originalService
    },
    service: service,
    idx: idx,
    ref: ref,
    error: showServiceValidationWarning,
    disableStatusBadge: disableStatusBadge,
    agreementData: agreementData,
    disableEditBillUpfront: !isFreshlyAdded,
    isFreshlyAdded: isFreshlyAdded,
    disableEditBillingDay: !isFreshlyAdded && serviceBillingModel.isRepeatableManual(service.billing),
    disableEditCost: isExistingOneTimeInStages ? {
      disabledTooltip: disabledOneTimeInStagesTooltip
    } : false,
    disableEditBillingInitiation: disableEditBillingInitiation({
      originalService
    }),
    onDayOfMonthChange: onDayOfMonthChange,
    onDayOfWeekChange: onDayOfWeekChange,
    onDayOfYearChange: onDayOfYearChange,
    onBillUpfrontModeChange: onBillUpfrontModeChange,
    onCostChange: cost => updateService(current => changeServicePriceAndDiscount(current, cost, originalService)),
    onChangeService: nextService => onServiceChange(nextService.id, () => nextService),
    onIncludedServicesChange: onIncludedServicesChange,
    additionalSections: compact([!isFutureEffectiveDate(relationship.effectiveDate, getNow()) && showOnApprovalInfo(service, originalService) && {
      disableBorder: true,
      value: /*#__PURE__*/_jsx(Callout, {
        "data-testid": EditAgreementServiceTestIds.OnApprovalServiceInfo(testId),
        children: "Since this is a one-time, automatically billed service, an invoice will be issued once amendments are saved and payment will require client approval."
      })
    }, shouldShowDiscountExpirationCallout && {
      disableBorder: true,
      value: /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": EditAgreementServiceTestIds.CalloutDiscountExpiration(testId),
        children: "Changing the service's rate or discount will delete the discount expiration settings"
      })
    }, amendedNextBillingDate && {
      disableBorder: true,
      value: /*#__PURE__*/_jsx(Callout, {
        intent: "warning",
        "data-testid": EditAgreementServiceTestIds.AmendedNextBillingDate(testId),
        children: loadingAmendedNextBillingDate ? /*#__PURE__*/_jsx(Spinner, {
          centeredInBox: true,
          centerBoxHeight: 30,
          size: 30,
          "data-testid": EditAgreementServiceTestIds.AmendedNextBillingDateLoading(testId)
        }) : getAmendedNextBillingDateText(service, amendedNextBillingDate)
      })
    }, serviceAdvancedSettingsAdditionalSection]),
    footer: changeBullets.length > 0 && /*#__PURE__*/_jsx(AmendmentChanges, {
      testid: EditAgreementServiceTestIds.Changes(testId),
      items: changeBullets,
      existingNote: serviceModel.note(service),
      isUnsaved: isUnsaved,
      autoApprovalDate: autoApprovalDate,
      onDiscard: onDiscardChanges,
      onNoteChange: onNoteChange
    }),
    menu: !(serviceModel.isCompleted(service.status) || serviceModel.isTerminated(service.status)) && /*#__PURE__*/_jsxs(DropdownMenu, {
      target: /*#__PURE__*/_jsx(IconButton, {
        type: "button",
        "data-testid": EditAgreementServiceTestIds.Menu(testId),
        children: /*#__PURE__*/_jsx(MoreVertical, {})
      }),
      children: [/*#__PURE__*/_jsx(DropdownMenuItem, {
        iconStart: /*#__PURE__*/_jsx(Edit2Liner24, {}),
        "data-testid": EditAgreementServiceTestIds.Edit(testId),
        disabled: isExistingOneTimeInStages,
        tooltipProps: isExistingOneTimeInStages ? {
          title: disabledOneTimeInStagesTooltip
        } : undefined,
        onClick: onEditService,
        children: "Edit"
      }), (serviceModel.isAmended(service.status) || serviceModel.isApproved(service.status)) && /*#__PURE__*/_jsx(DropdownMenuItem, {
        iconStart: /*#__PURE__*/_jsx(Void, {}),
        "data-testid": EditAgreementServiceTestIds.Stop(testId),
        onClick: onStopService,
        children: "Stop"
      })]
    })
  });
});
export default EditAgreementService;